import Accordions from "@/common/components/Accordions";
import Checkbox from "@/common/components/Checkbox";
import { EmptyDataMessage } from "@/common/components/EmptyDataMessage";
import { ImageCarousel } from "@/common/components/ImageCarousel";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import ResumeItem from "@/common/components/ResumeItem";
import Skeleton from "@/common/components/Skeleton";
import { Pagination } from "@/common/components/Table/pagination";
import { solicitationKindTranslation } from '@/common/constants/maintenance/solicitationKind.constant';
import { useMaintenanceSolicitations } from "@/common/hooks/queries/useMaintenanceSolicitations";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { useInternalFieldArray } from "@/common/hooks/useInternalFieldArray";
import { MaintenanceSolicitationData } from "@/common/types/maintenance/maintenanceSolicitation";
import { formatDateTime } from "@/common/utils/formatters";
import { MaintenanceWorkflowValidationSchema } from "@/features/privateCompanies/schemas/maintenance/workflows/maintenanceWorkflow.schema";
import { CameraOffIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Tooltip } from "react-tooltip";
import { twMerge } from "tailwind-merge";

const MaintenanceWorkflowSelectSolicitations = () => {
  const scrollListRef = useRef<HTMLDivElement>(null);

  const { filterParams } = useFilterParams();

  const [page, setPage] = useState<number | undefined>(1);

  const {
    data: solicitations,
    meta: solicitationsMeta,
    isPending: solicitationsIsPending,
    isFetching: solicitationsIsFetching,
  } = useMaintenanceSolicitations<MaintenanceSolicitationData[]>({
    filters: {
      page: page || 1,
      equipmentId: filterParams.equipment,
    },
  });

  const formMethods = useFormContext<MaintenanceWorkflowValidationSchema>();

  const {
    fields: solicitationIds,
    append: appendSolicitationId,
    remove: removeSolicitationId,
  } = useInternalFieldArray({
    ...formMethods,
    name: 'solicitation_ids',
  });

  useEffect(() => {
    scrollListRef.current?.scrollTo({ top: 0 });
  }, [page]);

  return (
    <div>
      <div ref={scrollListRef} className="flex flex-col gap-2">
        {solicitationsIsFetching && new Array(10).fill(null).map((_, i) => (
          <div
            key={`solicitation-loading-${i}`}
            className="min-h-[68px] flex items-center gap-3 px-4 bg-white-100 rounded-sm"
          >
            <Skeleton className="size-6 rounded-lg bg-white-100" />

            <div className="flex flex-col gap-0.5">
              <Skeleton className="h-5 w-20 bg-white-100" />

              <Skeleton className="h-4 w-16 bg-white-100" />
            </div>
          </div>
        ))}

        {!solicitationsIsFetching && Number(solicitations.length) === 0 && (
          <EmptyDataMessage
            message="Não há solicitações para este equipamento"
            className="h-96"
          />
        )}

        {!solicitationsIsFetching && Number(solicitations.length) > 0 && (
          <Accordions
            accordionsClassName="gap-2"
            className="p-0"
            triggerClassName="px-4 py-4"
            contentClassName="px-4 pb-4"
            defaultFirstItemOpened
            items={solicitations.map((solicitation, index) => ({
              index,
              id: solicitation.id,

              head: (
                <div className="flex items-center">
                  <div
                    data-tooltip-id={
                      (Number(solicitation.objectives?.length) || 0) === 0
                        ? `${solicitation.id}-cannot-be-selected`
                        : undefined
                    }
                  >
                    <Checkbox
                      label=""
                      boxClassName="size-6"
                      checked={solicitationIds.includes(solicitation.id)}
                      disabled={(Number(solicitation?.objectives?.length) || 0) === 0}
                      onChange={(event) => {
                        if (event.currentTarget.checked) {
                          appendSolicitationId(solicitation.id);
                        } else {
                          removeSolicitationId(solicitationIds.indexOf(solicitation.id));
                        }
                      }}
                    />
                  </div>

                  <Tooltip
                    id={`${solicitation.id}-cannot-be-selected`}
                    place="bottom"
                    className="z-50"
                  >
                    Essa solicitação não pode ser selecionada pois<br /> não contém nenhum objeto
                  </Tooltip>

                  <div className="flex flex-col items-start">
                    <p>
                      {solicitation.id.split('-')[0].toUpperCase()}
                    </p>

                    <p className="text-xs text-white-300">
                      {solicitationKindTranslation[solicitation.kind] || solicitation.kind}

                      &nbsp;

                      •

                      &nbsp;

                      {Number(solicitation.objectives?.length) === 1
                        ? `1 Objeto para análise`
                        : `${Number(solicitation.objectives?.length)} Objetos para análise`}
                    </p>
                  </div>
                </div>
              ),
              body: (
                <div className="flex flex-col md:flex-row gap-3">
                  <div className="flex flex-col gap-2 w-48">
                    <ImageCarousel
                      files={solicitation.photos?.map((photo, i) => ({ id: `${solicitation}_${i}`, file: photo?.url })) || []}
                      className="size-48 border-none"
                      showRemoveButton={false}
                      Placeholder={(
                        <ImageWithPlaceholder
                          placeholderIcon={CameraOffIcon}
                          className="size-48 rounded-lg bg-white-800"
                          iconClassName="size-36"
                        />
                      )}
                      showExpandButton
                      showNavigationButtons
                    />

                    <ResumeItem
                      label="Autor"
                      value={solicitation.creator?.full_name || '--'}
                      labelClassName="text-xs"
                      valueClassName="text-sm"
                    />

                    <ResumeItem
                      label="Data e hora da abertura"
                      value={formatDateTime(new Date(solicitation.created_at))}
                      labelClassName="text-xs"
                      valueClassName="text-sm"
                    />

                    <ResumeItem
                      label="Descrição"
                      value={solicitation.observations || '--'}
                      labelClassName="text-xs"
                      valueClassName="text-sm"
                    />
                  </div>

                  <div className="flex-1">
                    {Number(solicitation.objectives?.length) > 0 ? (
                      <div className="flex flex-col gap-2 max-h-[380px] overflow-auto">
                        {solicitation.objectives?.map((objective) => (
                          <div key={objective.id}>
                            <div className="bg-white-100 p-4 rounded-lg flex items-center gap-2">
                              <div className="flex items-center gap-2">
                                <div className="bg-white-100 p-2 rounded-md">
                                  <p className="text-xs font-bold">
                                    {objective.system.display_name}
                                  </p>
                                </div>

                                <div className="flex flex-col items-start">
                                  <p className="font-bold">
                                    {objective.action.display_name}

                                    &nbsp;

                                    <span className="font-normal">
                                      em
                                    </span>

                                    &nbsp;

                                    {objective.part.display_name}
                                  </p>

                                  <p className="text-xs">
                                    {objective.action.description}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <EmptyDataMessage
                        className="h-full py-0"
                        imageHeight={180}
                        message="Essa solicitação não possui objetos de manutenção"
                      />
                    )}
                  </div>
                </div>
              ),
            }))}
          />
        )}
      </div>

      {!solicitationsIsPending ? (
        <div
          className={twMerge(
            'w-full px-4 py-2 bg-white-200 rounded-b-lg flex justify-between items-center',
            typeof solicitationsMeta.total === 'undefined' && 'justify-end',
          )}
        >
          {typeof solicitationsMeta.total !== 'undefined' ? (
            <p className="text-xs italic font-light">
              Mostrando {solicitations.length} resultados de {solicitationsMeta.total}
            </p>
          ) : null}
          {setPage && (
            <Pagination
              currentPage={page || 1}
              totalPages={solicitationsMeta.total_pages}
              setPage={setPage}
            />
          )}
        </div>
      ) : (
        <div className="h-8 w-full px-4 bg-white-200 rounded-b-lg flex justify-between items-center">
          <Skeleton className="h-4 w-40" />
        </div>
      )}
    </div>
  );
}

export default MaintenanceWorkflowSelectSolicitations;
